import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useNotification } from "../context/NotificationContext";
import { motion, AnimatePresence } from "framer-motion";
import {
  Loader2,
  ChevronDown,
  ChevronUp,
  History,
  Search,
  Trash2,
  MessageSquare,
  ArrowLeft,
} from "lucide-react";
import axios from "axios";
import Header from "./Header";
import "./OldDebate.css";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000/api";

const OldDebate = () => {
  const [debates, setDebates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDebate, setSelectedDebate] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedRounds, setExpandedRounds] = useState({});
  const [quickDebatePrompt, setQuickDebatePrompt] = useState("");
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { showError, confirm } = useNotification();

  useEffect(() => {
    if (isAuthenticated) {
      fetchDebates();
    } else {
      navigate("/login");
    }
  }, [isAuthenticated, navigate]);

  const fetchDebates = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/ai/debates`, {
        withCredentials: true,
      });
      // Filter debates - include chats with chatType='debate' or with debateRounds
      const allDebates = response.data.debates || [];
      const filtered = allDebates.filter(
        (debate) =>
          debate.chatType === "debate" ||
          (debate.debateRounds &&
            Array.isArray(debate.debateRounds) &&
            debate.debateRounds.length > 0),
      );
      setDebates(filtered);
    } catch (error) {
      console.error("Error fetching debates:", error);
    } finally {
      setLoading(false);
    }
  };

  const deleteDebate = async (chatId, e) => {
    e.stopPropagation();
    const confirmed = await confirm({
      title: "Delete Debate",
      message:
        "Are you sure you want to delete this debate? This action cannot be undone.",
      confirmText: "Delete",
      cancelText: "Cancel",
      type: "danger",
    });

    if (!confirmed) {
      return;
    }

    try {
      await axios.delete(`${API_URL}/ai/chats/${chatId}`, {
        withCredentials: true,
      });
      setDebates(debates.filter((d) => d.chatId !== chatId));
      if (selectedDebate?.chatId === chatId) {
        setSelectedDebate(null);
      }
    } catch (error) {
      console.error("Error deleting debate:", error);
      showError("Failed to delete debate");
    }
  };

  const toggleRound = (roundIndex) => {
    setExpandedRounds((prev) => ({
      ...prev,
      [roundIndex]: !prev[roundIndex],
    }));
  };

  const filteredDebates = debates.filter(
    (debate) =>
      debate.query?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      debate.title?.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const formatDate = (dateString) => {
    if (!dateString) return "Unknown";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const handleQuickDebate = () => {
    console.log("Quick debate prompt:", quickDebatePrompt);
    return;
    if (quickDebatePrompt.trim()) {
      navigate("/debate", { state: { prompt: quickDebatePrompt.trim() } });
    }
  };

  return (
    <div className="old-debate-layout">
      <Header />

      <div className="old-debate-page">
        <div className="old-debate-header">
          <div className="old-debate-header-top">
            <button
              onClick={() => navigate("/debate")}
              className="old-debate-back-btn"
            >
              <ArrowLeft size={20} />
              Back to Debate Mode
            </button>
            <h1>Past Debate Sessions</h1>
          </div>
          <p className="old-debate-subtitle">
            Review your previous AI debate sessions and explore the multi-round
            arguments from Claude, ChatGPT, and Gemini.
          </p>

          {/* Quick debate input */}
          <div className="old-debate-quick-input">
            <input
              type="text"
              placeholder="Enter a legal question to start a new debate..."
              value={quickDebatePrompt}
              onChange={(e) => setQuickDebatePrompt(e.target.value)}
              onKeyPress={(e) => e.key === "Enter" && handleQuickDebate()}
              className="old-debate-quick-input-field"
            />
            <button
              onClick={handleQuickDebate}
              disabled={!quickDebatePrompt.trim()}
              className="old-debate-quick-btn"
            >
              Start Debate
            </button>
          </div>
        </div>

        <div className="old-debate-container">
          {/* Left sidebar - Debate list */}
          <div className="old-debate-sidebar">
            <div className="old-debate-search">
              <Search size={18} />
              <input
                type="text"
                placeholder="Search debates..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="old-debate-search-input"
              />
            </div>

            {loading ? (
              <div className="old-debate-loading">
                <Loader2 className="old-debate-spinner" />
                <span>Loading debates...</span>
              </div>
            ) : filteredDebates.length === 0 ? (
              <div className="old-debate-empty">
                <History size={48} />
                <p>No debates found</p>
                <button
                  onClick={() => navigate("/debate")}
                  className="old-debate-start-btn"
                >
                  Start Your First Debate
                </button>
              </div>
            ) : (
              <div className="old-debate-list">
                {filteredDebates.map((debate) => (
                  <div
                    key={debate.chatId}
                    className={`old-debate-item ${
                      selectedDebate?.chatId === debate.chatId ? "active" : ""
                    }`}
                    onClick={() => setSelectedDebate(debate)}
                  >
                    <div className="old-debate-item-header">
                      <h3 className="old-debate-item-title">
                        {debate.title ||
                          debate.query?.substring(0, 50) ||
                          "Untitled Debate"}
                      </h3>
                      <button
                        onClick={(e) => deleteDebate(debate.chatId, e)}
                        className="old-debate-delete-btn"
                        title="Delete debate"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                    <div className="old-debate-item-meta">
                      <span className="old-debate-item-date">
                        {formatDate(debate.updatedAt || debate.createdAt)}
                      </span>
                      {debate.maxRounds && (
                        <span className="old-debate-item-rounds">
                          {debate.maxRounds} rounds
                        </span>
                      )}
                    </div>
                    {debate.query && (
                      <p className="old-debate-item-preview">
                        {debate.query.substring(0, 100)}
                        {debate.query.length > 100 ? "..." : ""}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Right content - Debate details */}
          <div className="old-debate-content">
            {!selectedDebate ? (
              <div className="old-debate-placeholder">
                <MessageSquare size={64} />
                <h2>Select a debate to view details</h2>
                <p>
                  Choose a debate from the list to see all rounds and the final
                  consensus summary.
                </p>
              </div>
            ) : (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="old-debate-details"
              >
                <div className="old-debate-details-header">
                  <h2>
                    {selectedDebate.title ||
                      selectedDebate.query ||
                      "Untitled Debate"}
                  </h2>
                  <div className="old-debate-details-meta">
                    <span>
                      {formatDate(
                        selectedDebate.updatedAt || selectedDebate.createdAt,
                      )}
                    </span>
                    {selectedDebate.maxRounds && (
                      <span>• {selectedDebate.maxRounds} rounds</span>
                    )}
                  </div>
                </div>

                {selectedDebate.query && (
                  <div className="old-debate-query">
                    <h3>Original Question</h3>
                    <p>{selectedDebate.query}</p>
                  </div>
                )}

                {/* Debate Rounds */}
                {selectedDebate.debateRounds &&
                  selectedDebate.debateRounds.length > 0 && (
                    <div className="old-debate-rounds">
                      <h3>Debate Rounds</h3>
                      {selectedDebate.debateRounds.map((round, index) => (
                        <div key={index} className="old-debate-round">
                          <button
                            onClick={() => toggleRound(index)}
                            className="old-debate-round-header"
                          >
                            <span className="old-debate-round-title">
                              Round {round.round}
                            </span>
                            {expandedRounds[index] ? (
                              <ChevronUp size={20} />
                            ) : (
                              <ChevronDown size={20} />
                            )}
                          </button>
                          <AnimatePresence>
                            {expandedRounds[index] && (
                              <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: "auto", opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                className="old-debate-round-content"
                              >
                                {Object.entries(round.arguments || {}).map(
                                  ([model, text]) => (
                                    <div
                                      key={model}
                                      className="old-debate-argument"
                                    >
                                      <h4 className="old-debate-argument-model">
                                        {model}
                                      </h4>
                                      <div className="old-debate-argument-text">
                                        {text}
                                      </div>
                                    </div>
                                  ),
                                )}
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>
                      ))}
                    </div>
                  )}

                {/* Final Summary */}
                {selectedDebate.finalSummary && (
                  <div className="old-debate-summary">
                    <h3>Claude Final Consensus Summary</h3>
                    <div className="old-debate-summary-content">
                      {selectedDebate.finalSummary}
                    </div>
                  </div>
                )}

                {/* Quick access button to start new debate with same prompt */}
                <div className="old-debate-actions">
                  <button
                    onClick={() =>
                      navigate("/debate", {
                        state: { prompt: selectedDebate.query },
                      })
                    }
                    className="old-debate-restart-btn"
                  >
                    Start New Debate with This Question
                  </button>
                </div>
              </motion.div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OldDebate;
